import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import Plotly from 'plotly.js-dist-min';

type DiseaseId = 'FMD' | 'LSD' | 'SGP' | 'PPR';
type SpeciesId = 'cattle' | 'sheep' | 'goat' | 'pig' | 'buffalo' | 'wild';

const DISEASES: { id: DiseaseId; label: string }[] = [
  { id: 'FMD', label: 'FMD' },
  { id: 'LSD', label: 'LSD' },
  { id: 'SGP', label: 'SPGP' },
  { id: 'PPR', label: 'PPR' },
];

/** Species relevant to serological surveillance per disease */
const DISEASE_SPECIES: Record<
  DiseaseId,
  { id: SpeciesId; label: string; census: string | null; tested: string; pos: string }[]
> = {
  FMD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', tested: 'cattle_tested', pos: 'cattle_sero_fmd' },
    { id: 'sheep', label: 'Sheep', census: 'sheep', tested: 'sheep_tested', pos: 'sheep_sero_fmd' },
    { id: 'goat', label: 'Goats', census: 'goat', tested: 'goat_tested', pos: 'goat_sero_fmd' },
    { id: 'pig', label: 'Pigs', census: 'pig', tested: 'pig_tested', pos: 'pig_sero_fmd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', tested: 'buffalo_tested', pos: 'buffalo_sero_fmd' },
    { id: 'wild', label: 'Wild', census: null, tested: 'wild_tested', pos: 'wild_sero_fmd' },
  ],
  LSD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', tested: 'cattle_tested', pos: 'cattle_sero_lsd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', tested: 'buffalo_tested', pos: 'buffalo_sero_lsd' },
  ],
  SGP: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', tested: 'sheep_tested', pos: 'sheep_sero_sgp' },
    { id: 'goat', label: 'Goats', census: 'goat', tested: 'goat_tested', pos: 'goat_sero_sgp' },
  ],
  PPR: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', tested: 'sheep_tested', pos: 'sheep_sero_ppr' },
    { id: 'goat', label: 'Goats', census: 'goat', tested: 'goat_tested', pos: 'goat_sero_ppr' },
  ],
};

const COLORS = {
  positive: '#f5b4b4',
  testedNeg: '#a8d4ef',
  notTested: '#e5e7eb',
};

/** Min display width (%) so small Positive / Tested shares stay visible */
const MIN_SEGMENT_DISPLAY_PCT = 2.5;

/**
 * Widen Positive and Tested when > 0 but tiny; take the extra from the grey
 * (not tested) segment first so the bar still sums to ~100%.
 */
function displayStackedPcts(
  posTrue: number,
  testedTrue: number,
  notTrue: number,
): { pos: number; mid: number; not: number } {
  const pos = posTrue > 0 ? Math.max(posTrue, MIN_SEGMENT_DISPLAY_PCT) : 0;
  const mid = testedTrue > 0 ? Math.max(testedTrue, MIN_SEGMENT_DISPLAY_PCT) : 0;
  const need = pos - posTrue + (mid - testedTrue);
  let not = Math.max(0, notTrue - need);
  let sum = pos + mid + not;
  if (sum > 100 && pos + mid > 0) {
    const scale = 100 / (pos + mid);
    return { pos: pos * scale, mid: mid * scale, not: 0 };
  }
  if (sum < 100) not += 100 - sum;
  return { pos, mid, not };
}

function n(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
}

interface NestRow {
  label: string;
  area: string;
  species: string;
  census: number;
  tested: number;
  positive: number;
  testedNeg: number;
  notTested: number;
}

function buildNestRows(serology: any[], disease: DiseaseId): NestRow[] {
  const specs = DISEASE_SPECIES[disease];
  const out: NestRow[] = [];
  for (const row of serology || []) {
    const area = String(row.district_province || row.district || '—');
    for (const sp of specs) {
      const censusRaw = sp.census ? n(row[sp.census]) : 0;
      let tested = n(row[sp.tested]);
      let positive = n(row[sp.pos]);
      // Wild has no census — whole bar = tested (or positives if that's all we have)
      const census = sp.census ? censusRaw : Math.max(tested, positive);
      if (tested > census && census > 0) tested = census;
      if (positive > tested) positive = tested;
      if (census <= 0 && tested <= 0 && positive <= 0) continue;
      const total = Math.max(census, tested, positive);
      const testedCap = Math.min(tested, total);
      const posCap = Math.min(positive, testedCap);
      out.push({
        label: `${area} · ${sp.label}`,
        area,
        species: sp.label,
        census: total,
        tested: testedCap,
        positive: posCap,
        testedNeg: Math.max(0, testedCap - posCap),
        notTested: Math.max(0, total - testedCap),
      });
    }
  }
  return out;
}

export interface CycleSerologyVizProps {
  serology: any[];
  quarter: number;
  year: number;
}

const CycleSerologyViz: React.FC<CycleSerologyVizProps> = ({ serology, quarter, year }) => {
  const [disease, setDisease] = useState<DiseaseId>('FMD');
  const chartRef = useRef<HTMLDivElement | null>(null);

  const nestRows = useMemo(() => buildNestRows(serology, disease), [serology, disease]);
  // Only rows with at least one animal tested — empty bars aren't useful
  const chartRows = useMemo(() => nestRows.filter((r) => r.tested > 0), [nestRows]);
  const diseaseLabel = DISEASES.find((d) => d.id === disease)?.label || disease;

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    if (!chartRows.length) {
      Plotly.purge(el);
      return;
    }

    // Unique y keys (Plotly needs uniqueness); tick labels omit repeated district names
    const yKeys = chartRows.map((_, i) => String(i));
    let prevArea: string | null = null;
    const yLabels = chartRows.map((r) => {
      if (r.area !== prevArea) {
        prevArea = r.area;
        return `${r.area} · ${r.species}`;
      }
      return r.species;
    });

    const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);
    const posTrue = chartRows.map((r) => pct(r.positive, r.census));
    const testedTrue = chartRows.map((r) => pct(r.testedNeg, r.census));
    const notTrue = chartRows.map((r) => pct(r.notTested, r.census));
    const display = posTrue.map((p, i) => displayStackedPcts(p, testedTrue[i], notTrue[i]));
    const posPct = display.map((d) => d.pos);
    const testedNegPct = display.map((d) => d.mid);
    const notPct = display.map((d) => d.not);
    const testedTotalPct = chartRows.map((r) => pct(r.tested, r.census));

    const fmtPct = (displayV: number, trueV: number) => {
      if (trueV <= 0 && displayV <= 0) return '';
      if (trueV > 0 && trueV < 3) return `${trueV < 1 ? trueV.toFixed(1) : trueV.toFixed(0)}%`;
      return displayV >= 3 ? `${trueV.toFixed(0)}%` : '';
    };

    const hoverBase = chartRows.map((r, i) => {
      const posOfTested = r.tested > 0 ? ((r.positive / r.tested) * 100).toFixed(1) : '—';
      return (
        `<b>${r.area}</b> · ${r.species}<br>` +
        `Total animals in epiunits visited this quarter: ${r.census.toLocaleString()} (100%)<br>` +
        `Serologically tested: ${r.tested.toLocaleString()} (${testedTotalPct[i].toFixed(1)}%)<br>` +
        `Serologically positive: ${r.positive.toLocaleString()} (${posTrue[i].toFixed(1)}%; ${posOfTested}% of tested)<br>` +
        `Tested, not positive: ${r.testedNeg.toLocaleString()} (${testedTrue[i].toFixed(1)}%)<br>` +
        `Not tested: ${r.notTested.toLocaleString()} (${notTrue[i].toFixed(1)}%)`
      );
    });

    const traces = [
      {
        type: 'bar',
        orientation: 'h',
        name: 'Serologically positive',
        y: yKeys,
        x: posPct,
        marker: { color: COLORS.positive, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: posPct.map((v, i) => fmtPct(v, posTrue[i])),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Tested, not positive',
        y: yKeys,
        x: testedNegPct,
        marker: { color: COLORS.testedNeg, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: testedNegPct.map((v, i) => fmtPct(v, testedTrue[i])),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Not tested (of animals in visited epiunits)',
        y: yKeys,
        x: notPct,
        marker: { color: COLORS.notTested, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: notPct.map((v, i) => fmtPct(v, notTrue[i])),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#6b7280' },
      },
    ];

    const rowH = 28;
    const height = Math.max(280, 80 + chartRows.length * rowH);
    const maxLabelLen = yLabels.reduce((m, s) => Math.max(m, String(s).length), 0);
    // Room for long district/province · species ticks (first letter was clipped at l:140)
    const leftMargin = Math.max(180, Math.min(360, Math.ceil(maxLabelLen * 7.2) + 24));

    Plotly.newPlot(
      el,
      traces,
      {
        barmode: 'stack',
        margin: { t: 16, r: 24, b: 48, l: leftMargin },
        height,
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { size: 12, color: '#374151' },
        xaxis: {
          title: {
            text: '% of total animals in epiunits visited this quarter',
            font: { size: 12 },
          },
          range: [0, 100],
          ticksuffix: '%',
          gridcolor: '#f3f4f6',
          zeroline: false,
        },
        yaxis: {
          autorange: 'reversed',
          automargin: true,
          ticklabeloverflow: 'allow',
          tickmode: 'array',
          tickvals: yKeys,
          ticktext: yLabels,
          tickfont: { size: 11 },
        },
        legend: {
          orientation: 'h',
          y: -0.12,
          x: 0,
          font: { size: 11 },
        },
        bargap: 0.25,
      },
      { responsive: true, displayModeBar: false },
    );

    return () => {
      try {
        Plotly.purge(el);
      } catch {
        /* ignore */
      }
    };
  }, [chartRows, disease]);

  return (
    <div className="space-y-4">
      <h3 className="text-2xl font-black text-gray-800 bg-green-100 px-4 py-3 rounded">
        Serological Testing — Quarter {quarter}, {year}
      </h3>

      <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
        Each bar is 100% of total animals in epiunits visited this quarter (not the regional census).
        Coloured segments show serologically positive, tested but not positive, and not tested.
        Small shares are widened slightly so they stay visible; hover shows the true %. Wild animals
        have no population figure, so for wild the bar is based on the number tested.
      </p>

      <div className="flex flex-wrap gap-2">
        {DISEASES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setDisease(d.id)}
            className={`nav-btn px-4 py-1.5 text-sm ${
              disease === d.id ? 'ring-2 ring-green-600 ring-offset-1' : 'opacity-80'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        {chartRows.length ? (
          <div ref={chartRef} style={{ width: '100%' }} />
        ) : (
          <p className="py-10 text-center text-sm text-gray-600 leading-relaxed max-w-xl mx-auto">
            {nestRows.length
              ? `No animals were serologically tested for ${diseaseLabel} in this quarter.`
              : `No serological testing data for ${diseaseLabel} in this quarter.`}
          </p>
        )}
      </div>
    </div>
  );
};

export default CycleSerologyViz;
